// Laying out Kanban columns (spec §13.6). Columns store their layout as
// ordinary updates (cards' x, y, w, h, rotation; the column's h), so these
// functions compute the ops that bring columns up to date: after a gesture,
// while cards are dragged over a column, and when repairing remote edits.
import type { BoardObject, Card, Frame, Op } from '../../shared/types.ts';
import { byPosition, cardWidthIn, COLUMN, isColumn, rows, stack } from '../../shared/columns.ts';
import type { Store } from './store.ts';

/** A card's height at a given width (cards are as tall as their content). */
export type HeightAt = (card: Card, w: number) => number;

export function columnsIn(store: Store, frameIds: Iterable<string>): Frame[] {
  const out: Frame[] = [];
  for (const id of frameIds) {
    const f = store.get(id);
    if (isColumn(f)) out.push(f);
  }
  return out;
}

/** The column each card in a column belongs to. */
export function columnParents(columns: readonly Frame[]): Map<string, string> {
  const parent = new Map<string, string>();
  for (const c of columns) for (const id of c.children) parent.set(id, c.id);
  return parent;
}

/**
 * A column's cards, top to bottom. A card whose centre has left the column
 * sideways or above its top isn't counted: someone is carrying it away, and
 * laying the column out must not snap it back under them. (Its membership
 * catches up when they let go.) Below is fine: cards grow downwards.
 */
export function members(store: Store, column: Frame): Card[] {
  const cards: Card[] = [];
  for (const id of column.children) {
    const o = store.get(id);
    if (o?.type !== 'card') continue;
    const cx = o.x + o.w / 2, cy = o.y + o.h / 2;
    if (cx < column.x || cx > column.x + column.w || cy < column.y) continue;
    cards.push(o);
  }
  return byPosition(cards);
}

export interface LayoutRequest {
  /** Columns whose cards to lay out. */
  columns: Iterable<string>;
  /** Explicit top-to-bottom order for some columns; their children are set to it. */
  order?: ReadonlyMap<string, readonly string[]>;
  /** Cards to leave out of every column (they're being dragged). */
  exclude?: ReadonlySet<string>;
  /** Room in one column where dragged cards would land. */
  gap?: { column: string; index: number; size: number };
  /** Re-check every row's shared height, not only the rows of `columns`. */
  allRows?: boolean;
  /** Smallest change worth an op (world units). */
  tolerance?: number;
}

interface Planned { cards: { card: Card; y: number; w: number; h: number }[]; height: number; x: number }

/**
 * Ops that lay out the requested columns and give each affected row its
 * shared height. Changes smaller than `tolerance` are left alone.
 */
export function layoutOps(store: Store, columns: readonly Frame[], heightAt: HeightAt, req: LayoutRequest): Op[] {
  const tol = req.tolerance ?? 0.01;
  const byId = new Map(columns.map((c) => [c.id, c]));
  const wanted = new Set([...req.columns].filter((id) => byId.has(id)));
  if (!wanted.size && !req.allRows) return [];

  const plan = (c: Frame): Planned => {
    const w = cardWidthIn(c.w);
    const explicit = req.order?.get(c.id);
    let cards = explicit
      ? explicit.flatMap((id) => { const o = store.get(id); return o?.type === 'card' ? [o] : []; })
      : members(store, c);
    if (req.exclude) cards = cards.filter((o) => !req.exclude!.has(o.id));
    const heights = cards.map((o) => (Math.abs(o.w - w) < 0.01 ? o.h : heightAt(o, w)));
    const gap = req.gap?.column === c.id ? req.gap : undefined;
    const s = stack(c.y, heights, gap && { index: Math.min(gap.index, cards.length), size: gap.size });
    return { cards: cards.map((card, i) => ({ card, y: s.tops[i]!, w, h: heights[i]! })), height: s.height, x: c.x + (c.w - w) / 2 };
  };

  const ops: Op[] = [];
  const plans = new Map<string, Planned>();
  const planOf = (id: string) => {
    let p = plans.get(id);
    if (!p) plans.set(id, (p = plan(byId.get(id)!)));
    return p;
  };

  for (const id of wanted) {
    const c = byId.get(id)!;
    const p = planOf(id);
    for (const { card, y, w, h } of p.cards) {
      const props: Partial<Card> = {};
      if (Math.abs(card.x - p.x) > tol) props.x = p.x;
      if (Math.abs(card.y - y) > tol) props.y = y;
      if (Math.abs(card.w - w) > tol) props.w = w;
      if (Math.abs(card.h - h) > tol) props.h = h;
      if (card.rotation !== 0) props.rotation = 0;
      if (Object.keys(props).length) ops.push({ t: 'update', id: card.id, props });
    }
    const explicit = req.order?.get(id);
    if (explicit) {
      const children = p.cards.map((x) => x.card.id);
      if (children.join() !== c.children.join()) ops.push({ t: 'update', id, props: { children } });
    }
  }

  // Rows share the tallest column's height.
  for (const row of rows(columns)) {
    if (!req.allRows && !row.some((id) => wanted.has(id))) continue;
    const height = Math.max(...row.map((id) => planOf(id).height));
    for (const id of row) {
      if (Math.abs(byId.get(id)!.h - height) > tol) ops.push({ t: 'update', id, props: { h: height } });
    }
  }
  return ops;
}

/** Where cards dropped with their top card's centre at `centreY` would go among a column's other cards. */
export function dropIndex(store: Store, column: Frame, heightAt: HeightAt, exclude: ReadonlySet<string>, centreY: number): number {
  const w = cardWidthIn(column.w);
  const others = members(store, column).filter((o) => !exclude.has(o.id));
  const heights = others.map((o) => (Math.abs(o.w - w) < 0.01 ? o.h : heightAt(o, w)));
  const { tops } = stack(column.y, heights);
  let i = 0;
  while (i < others.length && tops[i]! + heights[i]! / 2 < centreY) i++;
  return i;
}

/** How much room dragged cards need in a column: their heights at its width, with gaps between. */
export function roomFor(cards: readonly Card[], column: Frame, heightAt: HeightAt): number {
  const w = cardWidthIn(column.w);
  return cards.reduce((sum, o) => sum + (Math.abs(o.w - w) < 0.01 ? o.h : heightAt(o, w)), 0) + Math.max(0, cards.length - 1) * COLUMN.gap;
}

/** The column under a point (topmost if several), if any. */
export function columnAt(store: Store, columns: readonly Frame[], x: number, y: number): Frame | null {
  let best: Frame | null = null;
  for (const c of columns) {
    if (x >= c.x && x <= c.x + c.w && y >= c.y && y <= c.y + c.h && (!best || store.rank(c.id) > store.rank(best.id))) best = c;
  }
  return best;
}

/** The "+ Add card" button at the bottom of a column (world rect). */
export function footerRect(c: Frame): { x: number; y: number; w: number; h: number } {
  return { x: c.x + COLUMN.pad, y: c.y + c.h - COLUMN.footer, w: c.w - 2 * COLUMN.pad, h: COLUMN.footer - 8 };
}

/** The neighbouring column in the same row, to the left (-1) or right (+1). */
export function neighbour(columns: readonly Frame[], column: Frame, dir: -1 | 1): Frame | null {
  const row = rows(columns).find((r) => r.includes(column.id));
  if (!row) return null;
  const id = row[row.indexOf(column.id) + dir];
  return id ? columns.find((c) => c.id === id) ?? null : null;
}

/** Columns in the same row to the right of `column`. */
export function columnsRightOf(columns: readonly Frame[], column: Frame): Frame[] {
  const row = rows(columns).find((r) => r.includes(column.id)) ?? [];
  return row.slice(row.indexOf(column.id) + 1).map((id) => columns.find((c) => c.id === id)!);
}

export const isCardIn = (o: BoardObject | undefined, column: Frame) => o?.type === 'card' && column.children.includes(o.id);
